// Helpers shared by the CPU strategies. Bots read the full state, so they must
// only look at what a human in that seat could see: their own hand, card
// counts, the board, and public scores.

import { TERRAIN_RESOURCE } from './board';
import { RESOURCES, type Resources } from './resources';
import { topologyOf } from './topology';
import type { GameState } from './types';

/** How many ways a token can be rolled out of 36 (2 and 12 are 1, 6 and 8 are 5). */
export function pips(token: number | null): number {
  return token === null ? 0 : 6 - Math.abs(7 - token);
}

/** Pips per resource the player already collects from their buildings. */
export function production(state: GameState, player: number): Resources {
  const t = topologyOf(state);
  const out = { brick: 0, lumber: 0, wool: 0, grain: 0, ore: 0 };
  state.buildings.forEach((b, v) => {
    if (b?.owner !== player) return;
    for (const h of t.vertices[v]!.hexes) {
      const r = TERRAIN_RESOURCE[state.board.tiles[h]!.terrain];
      if (r) out[r] += pips(state.board.tiles[h]!.token) * (b.kind === 'city' ? 2 : 1);
    }
  });
  return out;
}

export function missing(hand: Resources, cost: Resources): Resources {
  const out = { brick: 0, lumber: 0, wool: 0, grain: 0, ore: 0 };
  for (const r of RESOURCES) out[r] = Math.max(0, cost[r] - hand[r]);
  return out;
}

export function best<T>(items: readonly T[], score: (x: T) => number): T | undefined {
  let top: T | undefined;
  let topScore = -Infinity;
  for (const x of items) {
    const s = score(x);
    if (s > topScore) {
      top = x;
      topScore = s;
    }
  }
  return top;
}

export function robberOnMe(state: GameState, player: number): boolean {
  const t = topologyOf(state);
  return t.hexVertices[state.robber]!.some((v) => state.buildings[v]?.owner === player);
}
